import { Component, OnInit, ViewChild, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import { cloneDeep, findIndex, has, isEqual, uniqBy } from 'lodash';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { WorkShiftsService } from '../work-shifts.service';
import { AuthService } from '../../auth/auth.service';

import { FixedPlanedScheduleModalComponent } from './modals/fixed-planed-schedule-modal/fixed-planed-schedule-modal.component';
import { WORK_SHIFTS_PATHS } from '../work-shifts';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { ResourceDistributionComponent } from './resource-distribution/resource-distribution.component';
import { Enums } from '../../../configs/Enums';
import { AuthGroupTypesEnum } from '../../auth/auth-group-types.enum';
import { SectorRole } from '../../sectors/sectors';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-work-shifts',
  templateUrl: './work-shifts.component.html',
  styleUrls: ['./work-shifts.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class WorkShiftsComponent implements OnInit {
  moment = require('moment');
  workShiftsBreadcrumbs = [{ text: 'Рабочие смены', url: null }];
  private readonly nonScheduleTabs = ['resourceDistribution', 'workRestrictionsRules'];
  workShifts = {
    data: {
      plotsList: [],
      scheduleTypesList: [],
      cellTypesList: [],
      cellTypesListWithOutRelacation: [],
      workPlacesList: [],
    },
    filter: {
      plotSelected: null,
      fullName: null,
      fullNameCopy: null,
      monthDate: {
        selectedDate: null,
        formattedDate: null,
        initDate: null,
      },
    },
    calendarMetadata: {
      will_be_fixed_in: 0,
      working_hours_total: 0,
      planned_schedule_fixed: false,
      calendar: null,
      editable_within_days: null,
      current_date: null,
    },
    users: null,
    tabActiveManual: 'planedTemplateFilter',
    activeTemplateFilterType: 'planedTemplateFilter',
    planedTemplateFilter: {
      isActiveTemplateFilter: false,
      isActiveTemplateFilterPreviewMode: false,
      type: 'planedTemplateFilter',
      workTypeSelected: null,
      startTemplatePeriod: {
        initDate: null,
        dateMillis: null,
        dateFormatted: null,
        validationError: false,
      },
      startTemplatePeriodOpts: { minDate: null, maxDate: null },
      endTemplatePeriod: {
        initDate: null,
        dateMillis: null,
        dateFormatted: null,
        validationError: false,
      },
      endTemplatePeriodOpts: { minDate: null, maxDate: null },
      cellTypeSelected: null,
      startWorkTime: { hour: 0, minute: 0 },
      endWorkTime: { hour: 0, minute: 0 },
      workPlaceSelected: null,
      comment: null,
      selectedUsers: [],
    },
    planedValidationState: {
      isScheduleValid: false,
      isScheduleChecked: false,
      scheduleCountErrors: 0,
      scheduleFirstInvalidUser: null,
    },
    actualTemplateFilter: {
      isActiveTemplateFilter: false,
      isActiveTemplateFilterPreviewMode: false,
      type: 'actualTemplateFilter',
      workTypeSelected: null,
      startTemplatePeriod: {
        initDate: null,
        dateMillis: null,
        dateFormatted: null,
        validationError: false,
      },
      startTemplatePeriodOpts: { minDate: null, maxDate: null },
      endTemplatePeriod: {
        initDate: null,
        dateMillis: null,
        dateFormatted: null,
        validationError: false,
      },
      endTemplatePeriodOpts: { minDate: null, maxDate: null },
      cellTypeSelected: null,
      startWorkTime: { hour: 0, minute: 0 },
      endWorkTime: { hour: 0, minute: 0 },
      workPlaceSelected: null,
      comment: null,
      selectedUsers: [],
      isShowTemplateFilterButton: true,
    },
    resourceDistribution: {
      isActiveTemplateFilter: false,
      isActiveTemplateFilterPreviewMode: false,
      type: 'resourceDistribution',
    },
    workRestrictionsRules: {
      isActiveTemplateFilter: false,
      isActiveTemplateFilterPreviewMode: false,
      type: 'workRestrictionsRules',
    },
  };
  params = {
    sector_id: '',
    full_name: '',
    int_begin: '',
  };
  planedValidationStateDefault = cloneDeep(this.workShifts.planedValidationState);
  workShiftsDefault = cloneDeep(this.workShifts);
  filterDataLoading = false;
  monthDataLoading = false;
  userDataLoading = false;
  saveCellsLoading = false;
  isDisabledFixedButton = true;
  public dateShortFormat = Enums.dates.shortFormat;
  public dateFullFormat = Enums.dates.fullFormat;
  private sector_id: string;
  private full_name: string;
  private int_begin: string;

  @ViewChild('resourceDistributionComponent') resourceDistributionComponent: ResourceDistributionComponent;
  resourceDistributionEmployeeSearch = '';

  constructor(
    private workShiftsService: WorkShiftsService,
    private breadcrumbsService: BreadcrumbsService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    public modalService: NgbModal
  ) {}

  public get isDispatcher(): boolean {
    return this.authService.is(AuthGroupTypesEnum.Dispatcher);
  }

  ngOnInit() {
    this.breadcrumbsService.updateBreadcrumbs(this.workShiftsBreadcrumbs);

    this.route.paramMap.subscribe((paramMap) => {
      this.sector_id = paramMap['params'].sector_id;
      this.full_name = paramMap['params'].full_name;
      this.int_begin = paramMap['params'].int_begin;

      this.params.sector_id = this.sector_id || '';
      this.params.full_name = this.full_name || '';
      this.params.int_begin = this.int_begin || '';

      this.workShifts.filter.monthDate.initDate = this.int_begin ? this.moment(this.int_begin)._d : new Date();

      this.getFilterData();
    });

    this.moment.updateLocale('ru', {
      months: ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'],
    });
  }

  exportCSV() {
    // Для вкладки "Распределение ресурса на районы" экспорт не поддерживается
    if (this.nonScheduleTabs.includes(this.workShifts.activeTemplateFilterType)) {
      return;
    }

    const params = {
      date: this.moment(this.workShifts.filter.monthDate.selectedDate).format(this.dateShortFormat),
      sector_id: this.workShifts.filter.plotSelected.sector_id || '',
      sch_type: this.workShifts.activeTemplateFilterType === 'planedTemplateFilter' ? 'planned' : 'actual',
      name: this.workShifts.filter.fullName || '',
      groups: this.authService.isGRSUser() ? [AuthGroupTypesEnum.ServiceEngineerGRS] : [AuthGroupTypesEnum.ServiceEngineer],
    };

    this.workShiftsService.downloadUsers(params).subscribe();
  }

  makeCalendar(date) {
    const monthDate = this.workShifts.filter.monthDate;

    monthDate.selectedDate = date;
    monthDate.formattedDate = this.moment(this.workShifts.filter.monthDate.selectedDate).format('MMMM YYYY');
    this.getMetaDataProductionCalendar();
  }

  resetPlannedValidation() {
    this.workShifts.planedValidationState = cloneDeep(this.planedValidationStateDefault);
  }

  getFilterData() {
    let params = { sector_id: null, role: this.authService.isGRSUser() ? SectorRole.GRRS : SectorRole.MT };

    if ([1, 2].indexOf(this.authService.getAuthKey('current_group')['id']) > -1 && this.params.sector_id) {
      params.sector_id = this.params.sector_id;
    } else {
      delete params.sector_id;
    }

    this.filterDataLoading = true;
    this.workShiftsService.getFilterUnionData(this.authService.getAuthKey('u_id'), params).subscribe(
      (response) => {
        const data = response['data'];
        const workShiftsData = this.workShifts.data;

        workShiftsData.cellTypesList = response[0]['data'];
        workShiftsData.scheduleTypesList = response[1]['data'];
        workShiftsData.plotsList = response[2]['data'];
        workShiftsData.workPlacesList = response[3]['data'];

        if (this.params.sector_id) {
          let sectorIndex = findIndex(workShiftsData.plotsList, (s) => s.sector_id === +this.params.sector_id);

          if (sectorIndex > -1) {
            // this.workShifts.filter.plotSelected = workShiftsData.plotsList[sectorIndex];
            this.onChangePlotSelected(workShiftsData.plotsList[sectorIndex]);
          } else {
            // this.workShifts.filter.plotSelected = workShiftsData.plotsList[0];
            this.onChangePlotSelected(workShiftsData.plotsList[0]);
          }
        } else {
          // this.workShifts.filter.plotSelected = workShiftsData.plotsList[0];
          this.onChangePlotSelected(workShiftsData.plotsList[0]);
        }

        this.initUiData();
        this.filterDataLoading = false;
      },
      (error) => {
        console.log(error);
        this.filterDataLoading = false;
      }
    );
  }

  getMetaDataProductionCalendar() {
    let date = this.moment(this.workShifts.filter.monthDate.selectedDate).format(this.dateShortFormat);
    this.onResetTemplateFilter(false);

    this.monthDataLoading = true;
    this.workShiftsService
      .getMetaDataProductionCalendar({
        date,
        sector_id: this.workShifts.filter.plotSelected.sector_id,
      })
      .subscribe(
        (response) => {
          this.workShifts.calendarMetadata = response['data'];
          this.checkActiveTab();
        },
        (error) => {
          console.log(error);
          this.monthDataLoading = false;
        }
      );
  }

  getUsers() {
    if (this.userDataLoading) {
      return;
    }

    const params = {
      date: this.moment(this.workShifts.filter.monthDate.selectedDate).format(this.dateShortFormat),
      sector_id: this.workShifts.filter.plotSelected.sector_id || '',
      sch_type: this.workShifts.activeTemplateFilterType === 'planedTemplateFilter' ? 'planned' : 'actual',
      name: this.workShifts.filter.fullName || '',
      groups: this.authService.isGRSUser() ? [AuthGroupTypesEnum.ServiceEngineerGRS] : [AuthGroupTypesEnum.ServiceEngineer],
    };

    this.resetPlannedValidation();
    this.monthDataLoading = true;
    this.userDataLoading = true;
    this.workShiftsService.getUsers(params).subscribe(
      (response) => {
        this.workShifts.users = response['data'];

        this.workShifts.users.forEach((user) => {
          user.working_shift = this.workShiftsService.formatWorkingShift(user.working_shift);

          // Удаляем дубликаты ПОСЛЕ formatWorkingShift, так как он может создавать новые записи
          if (user.working_shift && Array.isArray(user.working_shift)) {
            // Приоритет: записи с ws_id (существующие) над записями без ws_id (новые)
            const sortedShifts = user.working_shift.sort((a, b) => {
              if (a.workday !== b.workday) return 0;
              return (b.ws_id ? 1 : 0) - (a.ws_id ? 1 : 0);
            });
            user.working_shift = uniqBy(sortedShifts, 'workday');
          }
          user.working_shift.forEach((work_shift) => {
            work_shift.is_editable = !(
              work_shift.workday <
              this.moment(this.workShifts.calendarMetadata.current_date).add(-this.workShifts.calendarMetadata.editable_within_days, 'day').format(this.dateShortFormat)
            );

            // Очищаем поле relocation для ячеек, которые имеют расписание
            // Это предотвращает отображение "-" у выбранных сотрудников после сохранения
            if (work_shift.schedule && work_shift.schedule.cell_type) {
              work_shift.relocation = false;
            }
          });
        });

        this.monthDataLoading = false;
        this.userDataLoading = false;
      },
      (error) => {
        console.log(error);
        this.workShifts.users = [];

        this.monthDataLoading = false;
        this.userDataLoading = false;
      }
    );
  }

  checkActiveTab() {
    if (this.workShifts.calendarMetadata.planned_schedule_fixed) {
      setTimeout(() => {
        this.workShifts.tabActiveManual = this.workShifts.actualTemplateFilter.type;
        this.workShifts.activeTemplateFilterType = this.workShifts.actualTemplateFilter.type;
        this.getUsers();
      }, 4);
    } else {
      setTimeout(() => {
        this.workShifts.tabActiveManual = this.workShifts.planedTemplateFilter.type;
        this.workShifts.activeTemplateFilterType = this.workShifts.planedTemplateFilter.type;
        this.getUsers();
      }, 4);
    }
  }

  onChangeScheduleType(changeTabObj) {
    if (this.workShifts.activeTemplateFilterType === changeTabObj.currentTab.id || (changeTabObj.currentTab && !changeTabObj.previewTab)) {
      return;
    }

    this.onResetTemplateFilter(false);
    this.workShifts.activeTemplateFilterType = changeTabObj.currentTab.id;

    // Для вкладок без графика (не табличных) не нужно загружать пользователей и инициализировать UI данные
    if (this.nonScheduleTabs.includes(this.workShifts.activeTemplateFilterType)) {
      this.monthDataLoading = false;
      this.userDataLoading = false;
      return;
    }

    this.initUiData();
    setTimeout(() => {
      this.getUsers();
    }, 4);
  }

  initUiData() {
    let templateFilterType = this.workShifts.activeTemplateFilterType;

    // Для вкладок без графика (не табличных) не нужна инициализация UI данных
    if (this.nonScheduleTabs.includes(templateFilterType)) {
      return;
    }

    let cellTypeIndex = findIndex(this.workShifts.data.cellTypesList, (cell) => cell.ct_id === 1);
    let scheduleIndex = findIndex(this.workShifts.data.scheduleTypesList, (schedule) => schedule.st_id === 1);
    let cellTypesListWithOutRelacation = [];

    if (this.params.full_name) {
      this.workShifts.filter.fullName = this.params.full_name;
    }

    if (cellTypeIndex > -1) {
      this.workShifts[this.workShifts.activeTemplateFilterType].workTypeSelected = this.workShifts.data.cellTypesList[cellTypeIndex];
    }

    if (scheduleIndex > -1) {
      this.workShifts[this.workShifts.activeTemplateFilterType].cellTypeSelected = this.workShifts.data.scheduleTypesList[scheduleIndex];
    }

    // Работа с датами в контексте текущего месяца. Если попадаем на переходной период: 1, 2 число,
    // а редактируем в прошлое на -2 дня, поэтому ставим minDate на начало месяца. Редактирование
    // -2 для прошлого месяца в контексте предыдущего месяца.
    if (templateFilterType === 'planedTemplateFilter') {
      let selectedDate = this.workShifts.filter.monthDate.selectedDate;

      if (selectedDate) {
        this.workShifts['planedTemplateFilter'].startTemplatePeriod.initDate = this.moment(this.moment(selectedDate).startOf('month').format('YYYY-MM-DD HH:MM:ss'))._d;
        this.workShifts['planedTemplateFilter'].endTemplatePeriod.initDate = this.moment(this.moment(selectedDate).endOf('month').format('YYYY-MM-DD HH:MM:ss'))._d;
        this.workShifts['planedTemplateFilter'].startTemplatePeriodOpts.minDate = this.moment(selectedDate).startOf('month').add(-1, 'day').format('YYYY-MM-DD HH:MM:ss');
        this.workShifts['planedTemplateFilter'].startTemplatePeriodOpts.maxDate = this.moment(selectedDate).endOf('month').format('YYYY-MM-DD HH:MM:ss');
        this.workShifts['planedTemplateFilter'].endTemplatePeriodOpts.minDate = this.moment(selectedDate).startOf('month').add(-1, 'day').format('YYYY-MM-DD HH:MM:ss');
        this.workShifts['planedTemplateFilter'].endTemplatePeriodOpts.maxDate = this.moment(selectedDate).endOf('month').format('YYYY-MM-DD HH:MM:ss');
      }
    } else {
      let selectedDate = this.workShifts.filter.monthDate.selectedDate;
      let selectedDateStartDate = this.moment(selectedDate).startOf('month').format(this.dateShortFormat);
      let selectedDateEndDate = this.moment(selectedDate).endOf('month').format(this.dateShortFormat);
      let selectedDatePrevMontStartDate = this.moment(selectedDate).add(-1, 'month').startOf('month').format('YYYY-MM-DD');
      let selectedDatePrevMontEndDate = this.moment(selectedDate).add(-1, 'month').endOf('month').format('YYYY-MM-DD');
      let prevDays = this.workShifts.calendarMetadata.editable_within_days;
      let currentDate = this.workShifts.calendarMetadata.current_date;
      let prevDaysDate = this.moment(currentDate).add(-prevDays, 'day').format(this.dateShortFormat);

      if (prevDaysDate >= selectedDateStartDate && prevDaysDate <= selectedDateEndDate) {
        this.workShifts.actualTemplateFilter.isShowTemplateFilterButton = true;

        this.workShifts['actualTemplateFilter'].startTemplatePeriod.initDate = this.moment(this.moment(prevDaysDate).add(1, 'day').format('YYYY-MM-DD HH:MM:ss'))._d;
        this.workShifts['actualTemplateFilter'].endTemplatePeriod.initDate = this.moment(this.moment(prevDaysDate).add(1, 'day').endOf('month').format('YYYY-MM-DD HH:MM:ss'))._d;
        this.workShifts['actualTemplateFilter'].startTemplatePeriodOpts.minDate = this.moment(prevDaysDate).format('YYYY-MM-DD HH:MM:ss');
        this.workShifts['actualTemplateFilter'].startTemplatePeriodOpts.maxDate = this.moment(prevDaysDate).endOf('month').format('YYYY-MM-DD HH:MM:ss');
        this.workShifts['actualTemplateFilter'].endTemplatePeriodOpts.minDate = this.moment(prevDaysDate).add(1, 'day').format('YYYY-MM-DD HH:MM:ss');
        this.workShifts['actualTemplateFilter'].endTemplatePeriodOpts.maxDate = this.moment(prevDaysDate).endOf('month').format('YYYY-MM-DD HH:MM:ss');
      } else if (prevDaysDate >= selectedDatePrevMontStartDate && prevDaysDate <= selectedDatePrevMontEndDate) {
        this.workShifts['actualTemplateFilter'].startTemplatePeriod.initDate = this.moment(this.moment(selectedDate).startOf('month').format('YYYY-MM-DD HH:MM:ss'))._d;
        this.workShifts['actualTemplateFilter'].endTemplatePeriod.initDate = this.moment(this.moment(selectedDate).endOf('month').format('YYYY-MM-DD HH:MM:ss'))._d;
        this.workShifts['actualTemplateFilter'].startTemplatePeriodOpts.minDate = this.moment(selectedDate).startOf('month').add(-1, 'day').format('YYYY-MM-DD HH:MM:ss');
        this.workShifts['actualTemplateFilter'].startTemplatePeriodOpts.maxDate = this.moment(selectedDate).endOf('month').format('YYYY-MM-DD HH:MM:ss');
        this.workShifts['actualTemplateFilter'].endTemplatePeriodOpts.minDate = this.moment(selectedDate).startOf('month').add(-1, 'day').format('YYYY-MM-DD HH:MM:ss');
        this.workShifts['actualTemplateFilter'].endTemplatePeriodOpts.maxDate = this.moment(selectedDate).endOf('month').format('YYYY-MM-DD HH:MM:ss');
      } else {
        this.workShifts.actualTemplateFilter.isShowTemplateFilterButton = false;
      }
    }

    this.workShifts.data.cellTypesList.forEach((cell) => {
      if (cell.ct_id !== 2) {
        cellTypesListWithOutRelacation.push(cell);
      }
    });

    this.workShifts.data.cellTypesListWithOutRelacation = cellTypesListWithOutRelacation;
  }

  onChangePlotSelected(selected) {
    this.workShifts.filter.plotSelected = selected;
    this.params.sector_id = selected.sector_id;
    this.router.navigate(['/', WORK_SHIFTS_PATHS.work_shifts, this.params]);

    // this.getMetaDataProductionCalendar();
  }

  onChangeVisibleTemplateFilter() {
    const templateFilterType = this.workShifts.activeTemplateFilterType;

    this.resetUsersSelected();
    this.workShifts[templateFilterType].isActiveTemplateFilter = !this.workShifts[templateFilterType].isActiveTemplateFilter;

    if (!this.workShifts[templateFilterType].isActiveTemplateFilter) {
      this.onResetTemplateFilter(false);
    } else {
      this.initUiData();
    }
  }

  onWorkShiftsDateChange(event) {
    if (event && event.value) {
      const params = cloneDeep(this.params);
      const date = event.value;

      this.params.int_begin = this.moment(date).format('YYYY-MM');
      this.router.navigate(['/', WORK_SHIFTS_PATHS.work_shifts, this.params]);

      if (isEqual(this.params, params)) {
        this.makeCalendar(date);
      } else {
        this.router.navigate(['/', WORK_SHIFTS_PATHS.work_shifts, this.params]);
      }
    }
  }

  onWorkShiftsPreviewDateChange() {
    const date = this.moment(this.workShifts.filter.monthDate.selectedDate).startOf('month').add(-1, 'month').format('YYYY-MM-DDTHH:mm');
    this.params.int_begin = this.moment(date).format('YYYY-MM');
    this.workShifts.filter.monthDate.selectedDate = this.moment(date)._d;
    this.router.navigate(['/', WORK_SHIFTS_PATHS.work_shifts, this.params]);
  }

  onWorkShiftsNextDateChange() {
    const date = this.moment(this.workShifts.filter.monthDate.selectedDate).startOf('month').add(1, 'month').format('YYYY-MM-DDTHH:mm');
    this.params.int_begin = this.moment(date).format('YYYY-MM');
    this.workShifts.filter.monthDate.selectedDate = this.moment(date)._d;
    this.router.navigate(['/', WORK_SHIFTS_PATHS.work_shifts, this.params]);
  }

  onSetCurrentDate() {
    this.params.int_begin = this.moment().startOf('month').format('YYYY-MM');
    this.router.navigate(['/', WORK_SHIFTS_PATHS.work_shifts, this.params]);
  }

  onResetTemplateFilter(isShowFilter) {
    const templateFilterType = this.workShifts.activeTemplateFilterType;

    // Для вкладок без графика (не табличных) применяем только "мягкий" сброс состояния, без работы с пользователями и UI данными
    if (this.nonScheduleTabs.includes(templateFilterType)) {
      this.workShifts[templateFilterType] = cloneDeep(this.workShiftsDefault[templateFilterType]);
      this.workShifts[templateFilterType].isActiveTemplateFilter = isShowFilter;
      return;
    }

    this.resetUsersSelected();
    this.workShifts[templateFilterType] = cloneDeep(this.workShiftsDefault[templateFilterType]);
    this.workShifts[templateFilterType].isActiveTemplateFilter = isShowFilter;
    this.initUiData();
  }

  resetUsersSelected() {
    // Для вкладок без графика (не табличных) не нужно сбрасывать выбранных пользователей
    if (this.nonScheduleTabs.includes(this.workShifts.activeTemplateFilterType)) {
      return;
    }

    this.workShifts[this.workShifts.activeTemplateFilterType].selectedUsers.forEach((user) => {
      user.isSelected = false;
    });
  }

  onOpenFixedPlanedScheduleConfirmDialog() {
    const fixedPlanedScheduleConfirmDialog = this.modalService.open(FixedPlanedScheduleModalComponent, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'modal-wrapper',
      backdrop: 'static',
    });

    fixedPlanedScheduleConfirmDialog.componentInstance.title = 'Подтвердите фиксацию плана';
    fixedPlanedScheduleConfirmDialog.componentInstance.description = 'После фиксации плана вы не сможете его' + ' отредактировать. Вы уверены, что хотите его зафиксировать?';
    fixedPlanedScheduleConfirmDialog.componentInstance.successButtonName = 'Да, зафиксировать';
    fixedPlanedScheduleConfirmDialog.componentInstance.abortButtonName = 'Отмена';
    fixedPlanedScheduleConfirmDialog.componentInstance.successCallbackAction = this.onFixedPlanedSchedule.bind(this);
    fixedPlanedScheduleConfirmDialog.componentInstance.currentDate = this.workShifts.calendarMetadata.current_date;
    fixedPlanedScheduleConfirmDialog.componentInstance.fixedDate = this.workShifts.filter.monthDate.initDate;

    return fixedPlanedScheduleConfirmDialog;
  }

  onSearchUsers() {
    this.workShifts.filter.fullNameCopy = this.workShifts.filter.fullName;
    this.params.full_name = this.workShifts.filter.fullName;
    this.router.navigate(['/', WORK_SHIFTS_PATHS.work_shifts, this.params]);
  }

  onResetName() {
    this.workShifts.filter.fullName = null;
    this.workShifts.filter.fullNameCopy = null;

    this.params.full_name = '';
    this.router.navigate(['/', WORK_SHIFTS_PATHS.work_shifts, this.params]);
  }

  onCheckStatePlanedSchedule() {
    let currentDateMonth = this.moment(this.workShifts.calendarMetadata.current_date).format('M');
    let lastSelectedMonthDate = this.moment(this.workShifts.filter.monthDate.selectedDate).format('M');
    this.workShifts.planedValidationState.scheduleFirstInvalidUser = null;
    this.workShifts.planedValidationState.scheduleCountErrors = 0;

    this.workShifts.users.forEach((user) => {
      let isValid = true;

      user.working_shift.forEach((workShift) => {
        if (
          (!workShift.schedule && !workShift.relocation) ||
          (workShift.schedule && has(workShift.schedule, 'cell_type') && workShift.schedule.cell_type === null && !workShift.relocation)
        ) {
          if (!this.workShifts.planedValidationState.scheduleFirstInvalidUser) {
            this.workShifts.planedValidationState.scheduleFirstInvalidUser = user;
          }

          isValid = false;
          workShift.isCellInvalid = true;
          this.workShifts.planedValidationState.scheduleCountErrors++;
        } else {
          workShift.isCellInvalid = false;
        }
      });

      if (!isValid) {
        user.isSomeCellInvalid = true;
      }
    });

    this.workShifts.planedValidationState.isScheduleChecked = true;
    this.workShifts.planedValidationState.isScheduleValid = !this.workShifts.planedValidationState.scheduleFirstInvalidUser;

    if (lastSelectedMonthDate - currentDateMonth > 1) {
      this.isDisabledFixedButton = true;
    }

    if (this.workShifts.planedValidationState.scheduleFirstInvalidUser) {
      let firstInvalidUserRow = document.getElementById(this.workShifts.planedValidationState.scheduleFirstInvalidUser.u_id);
      let firstInvalidUserRowTopPosition = this.getCoords(firstInvalidUserRow);

      window.scrollTo(0, firstInvalidUserRowTopPosition.top - 45);
    }
  }

  getCoords(elem) {
    let box = elem.getBoundingClientRect();
    let body = document.body;
    let docEl = document.documentElement;
    let scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop;
    let scrollLeft = window.pageXOffset || docEl.scrollLeft || body.scrollLeft;
    let clientTop = docEl.clientTop || body.clientTop || 0;
    let clientLeft = docEl.clientLeft || body.clientLeft || 0;
    let top = box.top + scrollTop - clientTop;
    let left = box.left + scrollLeft - clientLeft;

    return {
      top: top,
      left: left,
    };
  }

  onSaveTemplatePeriod() {
    this.onResetTemplateFilter(false);
    this.getUsers();
  }

  onPreloadTemplatePeriod(loading) {
    this.saveCellsLoading = loading;
  }

  onCheckFixedPlannedSchedule() {
    this.onOpenFixedPlanedScheduleConfirmDialog().result.then(
      () => {
        this.onResetTemplateFilter(false);
        this.getMetaDataProductionCalendar();
      },
      () => {}
    );
  }

  onFixedPlanedSchedule() {
    return this.workShiftsService.fixedPlannedSchedule({
      sector_id: this.workShifts.filter.plotSelected.sector_id,
      date: this.moment(this.workShifts.filter.monthDate.initDate).format(this.dateShortFormat),
    });
  }

  onSearchResourceDistributionEmployees() {
    if (this.resourceDistributionComponent) {
      this.resourceDistributionComponent.employeeSearchText = this.resourceDistributionEmployeeSearch;
      this.resourceDistributionComponent.applyFilter();
    }
  }

  onResetResourceDistributionSearch() {
    this.resourceDistributionEmployeeSearch = '';
    if (this.resourceDistributionComponent) {
      this.resourceDistributionComponent.employeeSearchText = '';
      this.resourceDistributionComponent.resetFilter();
    }
  }
}
